"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  FactsSection,
  RangesTable,
  SplitSection,
  SummaryStats,
} from "@/components/tools/cs-tools/ip-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  blockOf,
  formatAddress,
  hostRange,
  parseCidr,
  roleOf,
  type SplitMode,
  specialRanges,
} from "@/lib/tool-kit/cs-tools/ip";

/** English strings. A translated content file overrides these through `ui`. */
export const IP_SUBNET_CALCULATOR_STRINGS = {
  inputLabel: "IP address with prefix length or mask",
  inputHint:
    "IPv4 such as 192.168.1.10/24 or 192.168.1.10 255.255.255.0, or IPv6 such as 2001:db8:abcd::1/48. A zone suffix like %eth0 is ignored.",
  inputDefault: "192.168.1.10/24",
  errEmpty: "Enter an address with a prefix length, such as 192.168.1.10/24.",
  errTooLong: "That is too long to be an IP address with a prefix.",
  errInvalidAddress:
    "That is not a valid IP address. IPv4 needs four numbers from 0 to 255; IPv6 needs up to eight groups of hex digits.",
  errLeadingZero:
    "Leading zeros are ambiguous — some systems read 010 as octal. Write each number without them, such as 10.",
  errNoPrefix:
    "Add a prefix length such as /24, or a subnet mask such as 255.255.255.0.",
  errInvalidPrefix:
    "The prefix length must be a whole number: 0 to 32 for IPv4, 0 to 128 for IPv6.",
  errInvalidMask:
    "That is not a valid subnet mask. A mask is a run of ones followed by zeros, such as 255.255.254.0.",
  resultsHeading: "Result",
  statNetwork: "Network",
  statHosts: "Usable addresses",
  statAddresses: "Total addresses",
  statRole: "Your address is",
  roleNetwork: "The network address",
  roleBroadcast: "The broadcast address",
  roleHost: "A usable address",
  factsHeading: "Details",
  factsCaption: "Address and block details",
  colProperty: "Property",
  colValue: "Value",
  factAddress: "Address",
  factExpanded: "Expanded form",
  factMapped: "IPv4-mapped form",
  factPrefix: "Prefix length",
  factMask: "Subnet mask",
  factWildcard: "Wildcard mask",
  factNetwork: "Network address",
  factBroadcast: "Broadcast address",
  factFirst: "First usable address",
  factLast: "Last usable address",
  factFirstV6: "First address",
  factLastV6: "Last address",
  factSize: "Total addresses",
  factUsable: "Usable hosts",
  factClass: "Legacy class",
  factSubnets64: "Subnets of /64 inside",
  factHostBits: "Host bits",
  classValue: "Class {letter} (historical)",
  noBroadcast: "None",
  notApplicable: "Not applicable",
  smallerThan64: "None — the prefix is longer than /64",
  note31:
    "A /31 holds two addresses and both are usable on a point-to-point link (RFC 3021). It has no network or broadcast address.",
  note32: "A /32 is a single address: a host route or a loopback.",
  note127:
    "A /127 is the IPv6 equivalent of a /31 for links between two routers (RFC 6164).",
  noteLongPrefix:
    "A prefix longer than /64 leaves fewer than the 64-bit interface identifier that RFC 4291 expects on ordinary subnets, so address autoconfiguration will not work on it.",
  binaryHeading: "In binary",
  binaryCaption: "The address, mask and network in binary",
  binaryLegend:
    "Bold digits are the network part, the first {prefix} bits; faint digits are the host part.",
  rowAddress: "Address",
  rowMask: "Mask",
  rowNetwork: "Network",
  rangesHeading: "Special-purpose ranges",
  rangesBody:
    "Blocks that overlap the one you entered, from RFC 6890 and the IANA special-purpose registries. An empty list means it falls in ordinary public space.",
  rangesNone:
    "No special-purpose range overlaps this block, so it is ordinary public address space as far as these registries go.",
  rangesCaption: "Overlapping special-purpose ranges",
  colRange: "Range",
  colBlock: "Block",
  colRfc: "Defined in",
  colRelation: "Relationship",
  relInside: "The whole block is inside this range",
  relCovers: "The block contains this range",
  rangeThisNetwork: "This network",
  rangePrivate: "Private-use network",
  rangeSharedSpace: "Shared address space (carrier-grade NAT)",
  rangeLoopback: "Loopback",
  rangeLinkLocal: "Link-local",
  rangeIetfProtocol: "IETF protocol assignments",
  rangeTestNet: "Documentation (TEST-NET)",
  rangeRelay6to4: "6to4 relay anycast (deprecated)",
  rangeBenchmarking: "Benchmarking",
  rangeMulticast: "Multicast",
  rangeReserved: "Reserved for future use",
  rangeLimitedBroadcast: "Limited broadcast",
  rangeUnspecified: "Unspecified address",
  rangeMapped: "IPv4-mapped address",
  rangeNat64: "IPv4/IPv6 translation (NAT64)",
  rangeNat64Local: "Local-use IPv4/IPv6 translation",
  rangeDiscard: "Discard-only prefix",
  rangeTeredo: "Teredo tunnelling",
  rangeDocumentation: "Documentation",
  rangeSixToFour: "6to4",
  rangeGlobal: "Global unicast",
  rangeUniqueLocal: "Unique local address (private)",
  rangeSiteLocal: "Site-local (deprecated)",
  splitHeading: "Split into subnets",
  splitModeLabel: "Split by",
  splitModePrefix: "New prefix length",
  splitModeSubnets: "Number of subnets",
  splitModeHosts: "Hosts per subnet",
  splitValueLabel: "Value",
  splitHintPrefix: "A prefix length from /{min} to /{max}, such as 26.",
  splitHintSubnets: "How many subnets you need. Rounded up to a power of two.",
  splitHintHosts:
    "How many usable hosts each subnet must hold. The two reserved addresses are added for you.",
  splitDefaultPrefix: "26",
  splitDefaultSubnets: "4",
  splitDefaultHosts: "30",
  errSplitNumber: "Enter a whole number of 1 or more.",
  errSplitPrefixSmall:
    "The new prefix must be at least /{prefix}, the length of the block you are splitting.",
  errSplitHostsSmall: "This block is too small to hold that many hosts.",
  errSplitTooFine:
    "That needs a prefix longer than /{max}, which does not exist.",
  splitSummary:
    "{count} of /{prefix}: each has {size} addresses, {usable} usable.",
  subnetsWord: "one: subnet|other: subnets",
  splitRounded: "{requested} requested, rounded up to {count}.",
  splitLimited: "Showing the first {shown}.",
  splitCaption: "The subnets",
  colIndex: "#",
  colNetwork: "Network",
  colRangeUsable: "Usable range",
  colRangeAll: "Address range",
  colBroadcast: "Broadcast",
  colUsable: "Usable",
  copyNetwork: "Copy network",
  copySubnets: "Copy subnet list",
  copied: "Copied!",
} as const;

type Strings = typeof IP_SUBNET_CALCULATOR_STRINGS;

const SPLIT_DEFAULTS: Record<SplitMode, keyof Strings> = {
  prefix: "splitDefaultPrefix",
  subnets: "splitDefaultSubnets",
  hosts: "splitDefaultHosts",
};

const ERROR_KEYS = {
  empty: "errEmpty",
  tooLong: "errTooLong",
  invalidAddress: "errInvalidAddress",
  leadingZero: "errLeadingZero",
  noPrefix: "errNoPrefix",
  invalidPrefix: "errInvalidPrefix",
  invalidMask: "errInvalidMask",
} as const;

export default function IpSubnetCalculatorWidget() {
  const strings = useToolUi(IP_SUBNET_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [text, setText] = useState<string>(strings.inputDefault);
  const [mode, setMode] = useState<SplitMode>("prefix");
  const [splitText, setSplitText] = useState<string>(
    strings.splitDefaultPrefix
  );

  const result = parseCidr(text);
  const parsed = result.ok ? result.parsed : null;
  const block = parsed
    ? blockOf(parsed.family, parsed.address, parsed.prefix)
    : null;
  const effectiveMode: SplitMode =
    parsed?.family === 6 && mode === "hosts" ? "prefix" : mode;

  function changeMode(next: SplitMode) {
    setMode(next);
    setSplitText(strings[SPLIT_DEFAULTS[next]]);
  }

  const modeOptions: { value: SplitMode; label: string }[] = [
    { value: "prefix", label: strings.splitModePrefix },
    { value: "subnets", label: strings.splitModeSubnets },
  ];
  if (parsed?.family !== 6) {
    modeOptions.push({ value: "hosts", label: strings.splitModeHosts });
  }

  return (
    <div className="space-y-6">
      <TextField
        hint={strings.inputHint}
        id="ip-input"
        label={strings.inputLabel}
        mono
        onChange={setText}
        value={text}
      />
      <div aria-live="polite" className="space-y-5">
        {result.ok ? null : (
          <ErrorNotice>{strings[ERROR_KEYS[result.error]]}</ErrorNotice>
        )}
        {parsed && block ? (
          <>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            <SummaryStats
              block={block}
              fmt={fmt}
              locale={locale}
              range={hostRange(block)}
              role={roleOf(block, parsed.address)}
              strings={strings}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyNetwork}
                text={`${formatAddress(block.family, block.network)}/${block.prefix}`}
              />
            </div>
            <FactsSection
              address={parsed.address}
              block={block}
              fmt={fmt}
              locale={locale}
              range={hostRange(block)}
              strings={strings}
            />
            <RangesTable hits={specialRanges(block)} strings={strings} />
            <PillGroup
              label={strings.splitModeLabel}
              name="ip-split-mode"
              onChange={changeMode}
              options={modeOptions}
              value={effectiveMode}
            />
            <SplitSection
              block={block}
              fmt={fmt}
              locale={locale}
              mode={effectiveMode}
              onChange={setSplitText}
              strings={strings}
              value={splitText}
              valueNumber={parseIntegerInput(splitText)}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
